import { useState } from "preact/hooks";
import { signOut } from "../../app/boot";
import type { FullAuthResponse } from "../../api/types";
import { applyAuth, requireMe } from "../../state/auth";
import { toast } from "../../state/toasts";
import { AccountForm } from "../components/AccountForm";
import type { AccountMode } from "../components/AccountForm";
import { Button } from "../components/Button";
import { Modal } from "../components/Modal";
import { Tabs, panelId, tabId } from "../components/Tabs";

const TABS = [
  { id: "register", label: "Create account" },
  { id: "login", label: "Log in" },
] as const;

/**
 * Account dialog. Guests can register (progress is kept) or log into an existing account;
 * registered players can log out.
 */
export function AccountModal({ onClose }: { onClose: () => void }) {
  const me = requireMe();
  const [tab, setTab] = useState<AccountMode>("register");
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [busy, setBusy] = useState(false);

  const finish = (res: FullAuthResponse) => {
    applyAuth(res);
    toast(tab === "register" ? "Account created. Your progress is saved." : `Signed in as ${res.me.user.displayName}`, "success");
    onClose();
  };

  const logOut = async () => {
    setBusy(true);
    try {
      await signOut();
    } finally {
      setBusy(false);
    }
  };

  if (!me.user.isGuest) {
    return (
      <Modal
        key="account"
        title="Account"
        size="sm"
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button variant="danger" busy={busy} onClick={() => void logOut()}>
              Log out
            </Button>
          </>
        }
      >
        <p class="account-modal__lead">
          Signed in as <strong>@{me.user.username}</strong>
          <br />
          <span class="muted">Display name: {me.user.displayName}</span>
        </p>
      </Modal>
    );
  }

  if (confirmLogout) {
    return (
      <Modal
        key="confirm-logout"
        title="Leave this guest?"
        size="sm"
        onClose={() => setConfirmLogout(false)}
        footer={
          <>
            <Button variant="ghost" data-autofocus="" onClick={() => setConfirmLogout(false)}>
              Stay
            </Button>
            <Button variant="danger" busy={busy} onClick={() => void logOut()}>
              Log out anyway
            </Button>
          </>
        }
      >
        <p class="account-modal__lead">
          A guest account can only be reached from this browser. Logging out now abandons all of its levels, unlocks and
          rating. Create an account first to keep them.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      key="guest"
      title="Account"
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={() => setConfirmLogout(true)}>
          Log out
        </Button>
      }
    >
      <p class="account-modal__lead">
        You are playing as the guest <strong>{me.user.displayName}</strong>.
      </p>
      <Tabs
        tabs={TABS}
        active={tab}
        onChange={(id) => setTab(id === "login" ? "login" : "register")}
        idPrefix="account"
        ariaLabel="Account options"
      />
      <div
        class="account-modal__panel"
        role="tabpanel"
        id={panelId("account", tab)}
        aria-labelledby={tabId("account", tab)}
      >
        <p class="account-modal__note muted">
          {tab === "register"
            ? "Registering keeps every level, unlock and rating you have earned as a guest."
            : "Logging into an existing account leaves this guest account behind."}
        </p>
        <AccountForm mode={tab} initialDisplayName={me.user.displayName} onDone={finish} key={tab} />
      </div>
    </Modal>
  );
}
